import { DatePipe } from '@angular/common';
import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LetDirective } from '@ngrx/component';
import { Store } from '@ngrx/store';
import { combineLatestWith } from 'rxjs';

import { PrivaButtonComponent } from '@priva/components/button';
import { PrivaStatusCircleComponent } from '@priva/components/status-circle';
import { PrivaTableColumnDirective, PrivaTableComponent } from '@priva/components/table';
import { PrivaLocalizationModule } from '@priva/localization';
import { PrivaStatus } from '@priva/styles/foundation';

import { Registration, RegistrationType } from 'app/features/manual-input-registration/models';
import { DeviceInfoStorageService, MasterDataStorageService } from 'app/shared/services';
import { selectCachedContainerMeasurementsAsArray } from 'app/state/container-measurements/container-measurements.selectors';
import { selectCachedRegistrationsAsArray } from 'app/state/registrations/registrations.selectors';

import { RowRegistrationStatus } from '../../features/map-row-selector/models';
import { FileDownloadService } from '../../services/file-download.service';
import { formatUnixTimestampShort, getDateToday } from '../../utils/date.util';

@Component({
    selector: 'app-registrations-table',
    templateUrl: './registrations-table.component.html',
    styleUrl: './registrations-table.component.scss',
    imports: [
        DatePipe,
        PrivaButtonComponent,
        PrivaLocalizationModule,
        PrivaStatusCircleComponent,
        PrivaTableComponent,
        PrivaTableColumnDirective,
        PrivaButtonComponent,
        LetDirective,
    ],
})
export class RegistrationsTableComponent {
    private destroyRef = inject(DestroyRef);
    private store = inject(Store);
    private downloadFileService = inject(FileDownloadService);
    private masterDataStorageService = inject(MasterDataStorageService);
    private deviceInfoStorageService = inject(DeviceInfoStorageService);

    RegistrationType = RegistrationType;

    readonly cachedRegistrations$ = this.store.select(selectCachedRegistrationsAsArray);
    readonly cachedContainerMeasurement$ = this.store.select(selectCachedContainerMeasurementsAsArray);

    asRegistration = (row: unknown): Registration => row as Registration;

    getLocationStatus(status: RowRegistrationStatus): PrivaStatus {
        return status === 'done' ? 'Success' : 'Warning';
    }

    downloadRegistrations(): void {
        this.cachedRegistrations$
            .pipe(takeUntilDestroyed(this.destroyRef), combineLatestWith(this.cachedContainerMeasurement$))
            .subscribe(([registrations, containerMeasurements]) => {
                // Add a readable registration time to each registration
                const registrationsWithReadableTime = registrations.map((reg) => ({
                    ...reg,
                    registrationTimestamp: formatUnixTimestampShort(reg.registrationTimestamp),
                }));

                const containerMeasurementsWithReadableTime = containerMeasurements.map((cm) => ({
                    ...cm,
                    registrationTimestamp: formatUnixTimestampShort(cm.registrationTimestamp),
                }));

                const downloadData = {
                    deviceID: this.deviceInfoStorageService.getOrAddDeviceId(),
                    registrations: registrationsWithReadableTime,
                    containerMeasurements: containerMeasurementsWithReadableTime,
                };

                const deviceName = this.masterDataStorageService.getDeviceNameFromMasterDataStore();
                // Download as JSON with readable timestamps
                this.downloadFileService.download(
                    downloadData,
                    `registrations${getDateToday()}-${deviceName}.json`,
                );
            });
    }
}
